import { Link } from 'react-router-dom';
import BackButton from '../../components/ui/atoms/BackButton';

const PrivacyPolicy = () => (
  <div className="px-4 pb-28 pt-14 max-w-3xl mx-auto mt-10">
    <BackButton />
    <h1 className="text-2xl font-semibold mb-4">Privacy Policy</h1>
    <p className="mb-2">
      <strong>Effective date:</strong> June 11, 2025
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">1. Introduction</h2>
    <p>
      PanPals (“we,” “us,” or “our”) is committed to protecting your privacy.
      This Privacy Policy explains how we collect, use, store, and protect your
      personal data when you use our mobile app and website (collectively, the “Service”).
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">2. Data Controller</h2>
    <p>
      The data controller under the GDPR is:
      <br />
      Mohsen Nasiri, operator of PanPals
      <br />
      Address: c/o Mailboxde.com GmbH, Äußere Weberstr. 57, 02763 Zittau, Germany
      <br />
      Email:{' '}
      <Link 
        to="mailto:support@panpals.de"
        className="text-blue-600 underline hover:text-blue-800"
      >
        support@panpals.de
      </Link>
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">3. What We Collect</h2>
    <ul className="list-disc list-inside space-y-1">
      <li>
        <strong>Email address</strong> (required for account creation)
      </li>
      <li>
        <strong>Optional:</strong> Username, display name, profile image
      </li>
      <li>
        <strong>Recipe-related data:</strong> Recipes you create, view, bookmark, or review
      </li>
      <li>
        <strong>Feedback data:</strong> If you submit a bug report or feedback, we may collect device/browser metadata for diagnostics
      </li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">4. Legal Basis for Processing</h2>
    <ul className="list-disc list-inside space-y-1">
      <li>Art. 6(1)(b) GDPR – Contract: for account creation and providing app features</li>
      <li>Art. 6(1)(a) GDPR – Consent: for personalized dietary features and communication</li>
      <li>Art. 6(1)(f) GDPR – Legitimate interests: to maintain and improve the Service</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">5. How We Use Your Data</h2>
    <ul className="list-disc list-inside space-y-1">
      <li>To create and manage your user account</li>
      <li>To personalize your experience (e.g. dietary preferences)</li>
      <li>To provide and improve app functionality</li>
      <li>To respond to support requests and technical feedback</li>
      <li>To monitor and analyze app performance (analytics tools may be introduced in the future)</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">6. Data Storage & Security</h2>
    <p>
      All personal data is securely stored on infrastructure provided by trusted processors (e.g. Firebase, NEON, Cloudflare). All communication is encrypted via TLS. We do not use cookies or local storage. Profile and recipe images are publicly visible only in the context of user content.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">7. Data Sharing & Disclosure</h2>
    <ul className="list-disc list-inside space-y-1">
      <li>We do <strong>not</strong> sell, rent, or share personal data with third parties for advertising or marketing.</li>
      <li>We only share data with service providers when necessary for operation (e.g. Stripe for payments, Firebase for storage).</li>
    </ul>

    <h2 className="text-xl font-semibold mt-6 mb-2">8. International Data Transfers</h2>
    <p>
      Some processors may be located outside the EU. When data is transferred internationally, we rely on appropriate safeguards such as Standard Contractual Clauses (SCCs) under GDPR Article 46.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">9. Your Rights</h2>
    <ul className="list-disc list-inside space-y-1">
      {/* GDPR (EU) */}
      <li>
        <strong>EU (GDPR):</strong>  
        Right to access, rectify, or delete your personal data (Art. 15–17 GDPR);  
        right to data portability (Art. 20 GDPR);  
        right to object to processing or withdraw consent (Art. 7, 21 GDPR).
      </li>
      {/* CCPA (California) */}
      <li>
        <strong>California (CCPA):</strong>  
        Right to know what categories of personal data are collected and shared;  
        right to opt-out of sale of personal data (we do not sell your data);  
        right to request deletion of personal data.
      </li>
      {/* COPPA (US Children) */}
      <li>
        <strong>Children under 13 (COPPA):</strong>  
        Parent or guardian must consent before we collect data from minors under 13.
      </li>
      {/* PIPEDA (Canada) */}
      <li>
        <strong>Canada (PIPEDA):</strong>  
        Right to access and correct your personal data under PIPEDA.
      </li>
      {/* LGPD (Brazil) */}
      <li>
        <strong>Brazil (LGPD):</strong>  
        Right to confirmation of processing, access, correction, anonymization, or deletion of personal data.
      </li>
      {/* General complaint */}
      <li>
        Right to lodge a complaint with your local Data Protection Authority.
      </li>
    </ul>
    <p>
      <strong>EU Residents:</strong> You may contact your national Data Protection Authority (for Germany: Berliner Beauftragte für Datenschutz und Informationsfreiheit).
    </p>
    <p>
      <strong>California Residents:</strong> Contact the California Attorney General’s office for CCPA inquiries.
    </p>
    <p>
      For any questions or to exercise these rights, email us at{' '}
      <a
        href="mailto:support@panpals.de"
        className="text-blue-600 underline hover:text-blue-800"
      >
        support@panpals.de
      </a>
      .
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">10. Retention & Deletion</h2>
    <p>
      You can delete your account anytime via the app. Account data will be deleted within 30 days. Backups are purged after 90 days. Some anonymized, non-personal usage data may be retained for aggregate statistical analysis.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">11. Children</h2>
    <p>
      The app is not intended for children under the age of 16. We do not knowingly collect data from anyone under this age. If such data is discovered, please notify us immediately for removal.
    </p>

    <h2 className="text-xl font-semibold mt-6 mb-2">12. Updates to this Policy</h2>
    <p>
      We may update this Privacy Policy when features or legal requirements change. You will be informed of major changes via in-app notification or email.
    </p>
  </div>
);

export default PrivacyPolicy;
